// src/pages/LoginPage.js
import React, { useState } from 'react'
import { useNavigate, useOutletContext } from 'react-router-dom'
import axios from 'axios'
import { GoogleLogin } from '@react-oauth/google'

function LoginPage() {
  const navigate = useNavigate()
  const [form, setForm] = useState({ email: '', password: '' })
  const [error, setError] = useState(null)

  const { lang } = useOutletContext()

  const translations = {
    en: {
      pageTitle: 'Welcome Back!',
      subtitle: 'Sign in to continue to PayMe',
      emailLabel: 'Email',
      emailPlaceholder: 'Enter your email',
      passwordLabel: 'Password',
      passwordPlaceholder: 'Enter password',
      loginButton: 'Log In',
      errorMessageDefault: 'Login failed',
      noAccountQuestion: 'Don’t have an account?',
      registerLink: 'Register',
      brandTitle: 'PayMe',
      brandDescription:
        'Fast, secure, and user-friendly.\nSimplify your payment experience.',
    },
    // ... ja, pt, etc.
  }

  const t = translations[lang] || translations.en

  const handleChange = (e) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value,
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)

    try {
      const res = await axios.post(
        'https://payme-backend-713827320947.asia-northeast1.run.app/api/auth/login',
        {
          email: form.email,
          password: form.password,
        }
      )

      const { token } = res.data
      localStorage.setItem('token', token)

      const profileRes = await axios.get(
        'https://payme-backend-713827320947.asia-northeast1.run.app/api/users/profile',
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )

      localStorage.setItem('user', JSON.stringify(profileRes.data))
      navigate('/dashboard')
    } catch (err) {
      console.error(err)
      setError(err.response?.data?.message || t.errorMessageDefault)
    }
  }

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      const { credential } = credentialResponse
      if (!credential) {
        setError('Google authentication failed')
        return
      }

      // Send credential to backend
      const res = await axios.post(
        'https://payme-backend-713827320947.asia-northeast1.run.app/api/auth/google-login',
        { credential }
      )

      const { token, user } = res.data
      localStorage.setItem('token', token)
      localStorage.setItem('user', JSON.stringify(user))
      navigate('/dashboard')
    } catch (err) {
      console.error(err)
      setError(err.response?.data?.message || 'Google login failed')
    }
  }

  const handleGoogleError = () => {
    setError('Google Sign-In was cancelled or failed')
  }

  return (
    <div className="min-h-screen flex">
      {/* Left side */}
      <div
        className="hidden lg:flex lg:flex-1 bg-cover bg-center relative"
        style={{
          backgroundImage:
            'url("https://themesdesign.in/tailwick/assets/images/auth/modern-login-bg.jpg")',
        }}
      >
        <div className="w-full h-full bg-gradient-to-t from-blue-800 via-purple-600 to-indigo-800 bg-opacity-60 flex flex-col items-center justify-center p-8 text-center">
          <h1 className="text-white text-4xl font-bold mb-4 tracking-wide">
            {t.brandTitle}
          </h1>
          <p className="text-white text-sm max-w-xs mx-auto leading-relaxed whitespace-pre-wrap">
            {t.brandDescription}
          </p>
        </div>
      </div>

      {/* Right side */}
      <div className="flex flex-1 flex-col items-center justify-center px-6 py-12 bg-gray-50">
        <div className="w-full max-w-md space-y-6">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-700">{t.pageTitle}</h2>
            <p className="mt-1 text-sm text-gray-500">{t.subtitle}</p>
          </div>

          <div className="rounded-lg shadow-lg border border-gray-200 p-6 bg-white">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label
                  htmlFor="email"
                  className="block mb-1 text-gray-700 font-medium"
                >
                  {t.emailLabel}
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder={t.emailPlaceholder}
                />
              </div>
              <div>
                <label
                  htmlFor="password"
                  className="block mb-1 text-gray-700 font-medium"
                >
                  {t.passwordLabel}
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  value={form.password}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder={t.passwordPlaceholder}
                />
              </div>

              {error && (
                <p className="text-red-600 text-sm font-medium">{error}</p>
              )}

              <div>
                <button
                  type="submit"
                  className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700 transition-colors"
                >
                  {t.loginButton}
                </button>
              </div>
            </form>

            {/* Center the Google button */}
            <div className="my-4 text-center">
              <p className="text-gray-500 text-sm mb-2">OR</p>
              <div className="flex justify-center">
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={handleGoogleError}
                  text="continue_with"
                />
              </div>
            </div>
          </div>

          <div className="text-center">
            <p className="text-sm text-gray-500">
              {t.noAccountQuestion}{' '}
              <span
                onClick={() => navigate('/register')}
                className="text-blue-600 font-medium cursor-pointer hover:underline"
              >
                {t.registerLink}
              </span>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default LoginPage
